import { SkeletonCards, SkeletonTable } from '../../components/LoadingSkeleton/LoadingSkeleton';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import {
  ArrowLeft, ChevronRight, Download, MapPinned, Megaphone, Pause,
  Play, Route, Search, Sparkles, Volume2, X
} from 'lucide-react';
import './announcement.css';

const BASE = 'https://robopower.xyz';
const BIN_BASE = 'https://alcobrakeadmin.com/v3';
const GPS_BASE = 'https://alcobrakeadmin.com/v3';
const PAGE_SIZE = 10;
const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWxjb3Jha2UiLCJhIjoiY2tydnJub2JpMDllajJwb3h2NjdjbjFkdSJ9.6niQ3lJJ1OVEBMKj_BvFkA';

const storedUser = () => {
  for (const key of ['admin_user', 'fleetuser', 'alcotrackerparentuser', 'aargeeuser', 'user']) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      if (value) return value;
    } catch (_) { /* Ignore invalid legacy storage. */ }
  }
  return {};
};

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]).join('').toUpperCase() || 'RT';
const normalize = (value = '') => String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
const distanceMetres = (lat1, lng1, lat2, lng2) => {
  const radians = (value) => value * Math.PI / 180;
  const dLat = radians(lat2 - lat1);
  const dLng = radians(lng2 - lng1);
  const value = Math.sin(dLat / 2) ** 2 + Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
};
const formattedDistance = (metres) => metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(1)} km`;

const Announcement = () => {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStops, setLoadingStops] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [routeQuery, setRouteQuery] = useState('');
  const [routePage, setRoutePage] = useState(1);
  const [page, setPage] = useState(1);
  const [language, setLanguage] = useState('te');
  const [audio, setAudio] = useState({});
  const [generating, setGenerating] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [tracking, setTracking] = useState({ status: 'waiting', text: 'Waiting for vehicle location...', updated: '' });
  const [text, setText] = useState('');
  const audioRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const stopMarkersRef = useRef([]);
  const vehicleMarkerRef = useRef(null);
  const gpsTimerRef = useRef(null);

  const openAddRouteDialog = async () => {
  const { value: routeData } = await Swal.fire({
    title: 'Add Route',
    width: 560,
    customClass: { popup: 'ann-dialog', htmlContainer: 'ann-dialog-content' },
    html: `
      <div class="ann-form-grid">
        <label>From<input id="route-from" placeholder="Starting location" /></label>
        <label>To<input id="route-to" placeholder="Destination" /></label>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Next',
    cancelButtonText: 'Cancel',
    focusConfirm: false,
    preConfirm: () => {
      const from = document.getElementById('route-from')?.value?.trim();
      const to = document.getElementById('route-to')?.value?.trim();
      const clientid = 'CL0001';

      if (!from) {
        Swal.showValidationMessage('Please enter From');
        return false;
      }

      if (!to) {
        Swal.showValidationMessage('Please enter To');
        return false;
      }


      return {
        route_name: `${from} - ${to}`,
        from,
        to,
        clientid
      };
    }
  });

  if (!routeData) return;

  openStopsDialog(routeData);
};

  const openStopsDialog = async (routeData) => {
    const stopMarkup = () => `
      <div class="stop-card">
        <div class="ann-stop-header"><strong class="stop-title"></strong><button type="button" class="remove-stop-btn">Remove stop</button></div>
        <div class="ann-form-grid">
          <label>Sequence<input class="route-input seq" readonly /></label>
          <label>Stop name<input class="route-input stop_name" placeholder="Enter stop name" /></label>
          <label>Latitude<input class="route-input lat" type="number" step="any" min="-90" max="90" placeholder="17.4475" /></label>
          <label>Longitude<input class="route-input lon" type="number" step="any" min="-180" max="180" placeholder="78.3862" /></label>
        </div>
      </div>`;
    const { value } = await Swal.fire({
      title: '<span>Add Route Stops</span><span class="ann-route-context"></span>',
      width: 720,
      customClass: { popup: 'ann-dialog', htmlContainer: 'ann-dialog-content' },
      confirmButtonText: 'Save Route',
      showCancelButton: true,
      focusConfirm: false,
      html: `<p class="ann-dialog-description">Add stops in travel order. Each stop requires a name and valid coordinates.</p><div id="stopContainer"></div><button id="addStopBtn" type="button" class="ann-add-stop">+ Add another stop</button>`,
      didOpen: () => {
        const popup = Swal.getPopup();
        popup.querySelector('.ann-route-context').textContent = `${routeData.from} \u2192 ${routeData.to}`;
        const container = popup.querySelector('#stopContainer');
        const updateSequences = () => {
          const cards = container.querySelectorAll('.stop-card');
          cards.forEach((card, index) => {
            card.querySelector('.seq').value = index + 1;
            card.querySelector('.stop-title').textContent = `Stop #${index + 1}`;
            card.querySelector('.remove-stop-btn').disabled = cards.length === 1;
          });
        };
        const addStop = () => {
          container.insertAdjacentHTML('beforeend', stopMarkup());
          updateSequences();
          container.lastElementChild.querySelector('.stop_name').focus();
        };
        popup.querySelector('#addStopBtn').addEventListener('click', addStop);
        container.addEventListener('click', (event) => {
          const button = event.target.closest('.remove-stop-btn');
          if (!button || button.disabled) return;
          button.closest('.stop-card').remove();
          updateSequences();
        });
        addStop();
      },
      preConfirm: () => {
        const stops = [];
        for (const [index, card] of [...Swal.getPopup().querySelectorAll('.stop-card')].entries()) {
          const stop_name = card.querySelector('.stop_name').value.trim();
          const lat = card.querySelector('.lat').value.trim();
          const lon = card.querySelector('.lon').value.trim();
          if (!stop_name || !lat || !lon || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lon)) || Math.abs(Number(lat)) > 90 || Math.abs(Number(lon)) > 180) {
            Swal.showValidationMessage(`Stop #${index + 1}: enter a name, latitude between -90 and 90, and longitude between -180 and 180.`);
            return false;
          }
          stops.push({ sequence: index + 1, stop_name, lat, lon });
        }
        if (!stops.length) { Swal.showValidationMessage('Please add at least one stop.'); return false; }
        return { route: routeData, stops };
      }
    });
    if (!value) return;

  // API PAYLOAD

  const payload = {

    route_name: routeData.route_name,

    clientid: routeData.clientid,

    dealerid: Number(
      storedUser()?.dealerId ||
      storedUser()?.dealerid ||
      260
    ),

    stops: value.stops.map((stop) => ({

      stop_sequence:
        Number(stop.sequence),

      stop_name:
        stop.stop_name,

      lat:
        Number(stop.lat),

      lon:
        Number(stop.lon)

    }))

  };


  try {

    Swal.fire({

      title: 'Creating Route...',

      allowOutsideClick: false,

      allowEscapeKey: false,

      didOpen: () => {
        Swal.showLoading();
      }

    });


    const response = await axios.post(
      `${BASE}/app/vehicleroutes/createannouncementroutes`,
      payload
    );


    await Swal.fire({

      icon: 'success',

      title: 'Route Created',

      text: `Route ID : ${response.data?.route_id ?? ''}`,

      confirmButtonColor: '#10b981'

    });


    // Refresh routes

    const user = storedUser();

    const dealerId =
      user.dealerId ||
      user.dealerid ||
      260;

    const { data } = await axios.get(
      `${BASE}/app/vehicleroutes/getroutes`,
      {
        params: {
          dealer_id: dealerId
        }
      }
    );

    setRoutes(
      Array.isArray(data)
        ? data
        : data?.routes || []
    );


  } catch (error) {

    console.error(
      'Route creation failed:',
      error
    );


    Swal.fire({

      icon: 'error',

      title: 'Route creation failed',

      text:
        error?.response?.data?.message ||
        'Unable to create the route. Please try again.'

    });

  }

};

  useEffect(() => {
    let active = true;
    const user = storedUser();
    const dealerId = user.dealerId || user.dealerid || 260;
    setLoading(true);
    axios.get(`${BASE}/app/vehicleroutes/getroutes`, { params: { dealer_id: dealerId } })
      .then(({ data }) => { if (active) setRoutes(Array.isArray(data) ? data : data?.routes || []); })
      .catch(() => { if (active) setError('Routes could not be loaded. Please try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => () => {
    Object.values(audio).forEach((url) => URL.revokeObjectURL(url));
    audioRef.current?.pause();
  }, [audio]);

  const visibleRoutes = useMemo(() => {
    const term = routeQuery.trim().toLowerCase();
    return routes.filter((route) => !term || [route.route_name, route.id, route.direction]
      .some((value) => String(value ?? '').toLowerCase().includes(term)));
  }, [routeQuery, routes]);

  const routePages = Math.max(1, Math.ceil(visibleRoutes.length / PAGE_SIZE));
  const currentRoutePage = Math.min(routePage, routePages);
  const pageRoutes = visibleRoutes.slice((currentRoutePage - 1) * PAGE_SIZE, currentRoutePage * PAGE_SIZE);

  const visibleStops = useMemo(() => {
    const term = query.trim().toLowerCase();
    return stops.filter((stop) => !term || [stop.stop_name, stop.land_mark, stop.lat, stop.lon].some((value) => String(value || '').toLowerCase().includes(term)));
  }, [query, stops]);
  const pages = Math.max(1, Math.ceil(visibleStops.length / PAGE_SIZE));
  const pageStops = visibleStops.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  

  const loadRouteAudio = async (route, routeStops) => {
    try {
      const { data } = await axios.get(`${BASE}/app/vehicleroutes/getaudiozip`, { params: { route_id: route.id, type: 'mp3', lang: language }, responseType: 'arraybuffer' });
      const zip = await JSZip.loadAsync(data);
      const nextAudio = {};
      await Promise.all(Object.values(zip.files).map(async (file) => {
        if (file.dir || !file.name.toLowerCase().endsWith('.mp3')) return;
        const key = normalize(file.name.replace(/\.mp3$/i, '').split('/').pop());
        const stop = routeStops.find((item) => normalize(item.stop_name) === key || normalize(item.stop_name).includes(key) || key.includes(normalize(item.stop_name)));
        if (stop) nextAudio[stop.id ?? routeStops.indexOf(stop)] = URL.createObjectURL(await file.async('blob'));
      }));
      setAudio(nextAudio);
      return Object.keys(nextAudio).length > 0;
    } catch (_) { return false; }
  };

  const openRoute = async (route) => {
    setSelectedRoute(route); setStops([]); setQuery(''); setPage(1); setAudio({}); setError(''); setLoadingStops(true);
    setTracking({ status: 'waiting', text: 'Connecting to vehicle GPS...', updated: '' });
    try {
      const { data } = await axios.get(`${BASE}/app/vehicleroutes/getstops`, { params: { route_id: route.id } });
      const routeStops = Array.isArray(data) ? data : data?.stops || [];
      setStops(routeStops);
      loadRouteAudio(route, routeStops);
    } catch (_) { setError('Stops could not be loaded for this route.'); }
    finally { setLoadingStops(false); }
  };

  const generateRouteAudio = async () => {
    if (!selectedRoute) return;
    setGenerating(true); setError('');
    try {
      await axios.get(`${BASE}/py/generatewavs`, { params: { routeId: selectedRoute.id, type: 'mp3', lang: language } });
      const matched = await loadRouteAudio(selectedRoute, stops);
      if (!matched) setError('Audio was generated, but no files matched the stop names.');
    } catch (_) { setError('Audio could not be generated right now.'); }
    finally { setGenerating(false); }
  };

  const toggleAudio = (key) => {
    if (!audio[key]) return;
    if (playing === key) { audioRef.current?.pause(); setPlaying(null); return; }
    audioRef.current?.pause();
    const player = new Audio(audio[key]);
    audioRef.current = player; player.onended = () => setPlaying(null); player.play(); setPlaying(key);
  };

  const generateTextAudio = () => {
    if (!text.trim()) return;
    window.open(`${BASE}/py/generate-audio?message=${encodeURIComponent(text.trim())}&lang=${language}`, '_blank', 'noopener,noreferrer');
  };

  const downloadBin = (route) => window.open(`${BIN_BASE}/app/vehicleroutes/getroutebin?route_id=${encodeURIComponent(route.id)}`, '_blank', 'noopener,noreferrer');

  useEffect(() => {
    if (!selectedRoute || loadingStops || !mapContainerRef.current) return undefined;
    const points = stops.map((stop) => ({ lat: Number(stop.latitude ?? stop.lat ?? stop.stop_latitude), lng: Number(stop.longitude ?? stop.lng ?? stop.lon ?? stop.stop_longitude), name: stop.stop_name || 'Stop' })).filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const first = points[0] || { lat: 17.4475, lng: 78.3862 };
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark' || document.body.getAttribute('data-theme') === 'dark';
    const map = new mapboxgl.Map({ container: mapContainerRef.current, style: isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v11', center: [first.lng, first.lat], zoom: 12, attributionControl: false });
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.on('load', () => {
      stopMarkersRef.current = points.map((point, index) => {
        const el = document.createElement('div'); el.className = 'announcement-map-stop'; el.textContent = String(index + 1);
        return new mapboxgl.Marker({ element: el }).setLngLat([point.lng, point.lat]).setPopup(new mapboxgl.Popup({ offset: 18 }).setText(point.name)).addTo(map);
      });
      if (points.length > 1) {
        const bounds = points.reduce((value, point) => value.extend([point.lng, point.lat]), new mapboxgl.LngLatBounds([points[0].lng, points[0].lat], [points[0].lng, points[0].lat]));
        map.fitBounds(bounds, { padding: 55, maxZoom: 15 });
      }
    });

    let stopped = false;
    let inFlight = false;
    let lastAddress = '';
    let lastAddressPoint = null;
    const extractCbid = (client) => client?.cbid || client?.CBID || client?.Vehicle?.cbid || client?.vehicle?.cbid || client?.Device?.cbid;
    const extractGps = (payload) => {
      for (const candidate of [payload, payload?.data, payload?.result, payload?.latestPing, payload?.latest_ping]) {
        const row = Array.isArray(candidate) ? candidate[0] : candidate;
        const lat = Number(row?.lat ?? row?.latitude ?? row?.location?.lat);
        const lng = Number(row?.lon ?? row?.lng ?? row?.longitude ?? row?.location?.lon ?? row?.location?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { row, lat, lng };
      }
      return null;
    };

    const startGps = async () => {
      try {
        const { data } = await axios.get(`${BASE}/app/vehicleroutes/getassigneddevice`, { params: { route_id: selectedRoute.id } });
        if (stopped) return;
        const clients = Array.isArray(data?.data ?? data) ? (data?.data ?? data) : [data?.data ?? data].filter(Boolean);
        const cbid = clients.map(extractCbid).find(Boolean);
        if (!cbid) throw new Error('No vehicle is assigned to this route.');
        const poll = async () => {
          if (inFlight || stopped) return; inFlight = true;
          try {
            const response = await axios.get(`${GPS_BASE}/app/v2data/getlatestpingv22`, { params: { cbid, _: Date.now() } });
            if (stopped) return;
            const gps = extractGps(response.data); if (!gps) throw new Error('GPS coordinates are unavailable.');
            const timestamp = gps.row.gtime || gps.row.gps_time || gps.row.timestamp || new Date().toLocaleString();
            const parsedTimestamp = timestamp ? new Date(String(timestamp).replace(' ', 'T')).getTime() : NaN;
            const gpsStatus = Number.isFinite(parsedTimestamp) && Date.now() - parsedTimestamp > 120000 ? 'stale' : 'live';
            const nearest = points.reduce((best, point) => {
              const distance = distanceMetres(gps.lat, gps.lng, point.lat, point.lng);
              return !best || distance < best.distance ? { point, distance } : best;
            }, null);
            if (!lastAddressPoint || distanceMetres(gps.lat, gps.lng, lastAddressPoint.lat, lastAddressPoint.lng) >= 50) {
              try {
                const geocode = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${gps.lng},${gps.lat}.json`, { params: { access_token: MAPBOX_TOKEN, limit: 1, language: 'en' } });
                lastAddress = geocode.data?.features?.[0]?.place_name || geocode.data?.features?.[0]?.text || '';
                lastAddressPoint = { lat: gps.lat, lng: gps.lng };
              } catch (_) { /* Coordinates still provide a useful live location. */ }
            }
            if (stopped) return;
            const coordinateText = `${gps.lat.toFixed(6)}, ${gps.lng.toFixed(6)}`;
            const locationText = lastAddress ? `${lastAddress} - ${coordinateText}` : coordinateText;
            const nearestText = nearest ? ` - Near ${nearest.point.name} - ${formattedDistance(nearest.distance)} from stop` : '';
            setTracking({ status: gpsStatus, text: `${locationText}${nearestText}`, updated: String(timestamp) });
            if (!vehicleMarkerRef.current) {
              const el = document.createElement('div'); el.className = 'announcement-vehicle-marker'; el.innerHTML = '<span></span>';
              vehicleMarkerRef.current = new mapboxgl.Marker({ element: el }).setLngLat([gps.lng, gps.lat]).addTo(map);
              map.easeTo({ center: [gps.lng, gps.lat], zoom: Math.max(map.getZoom(), 14), duration: 900 });
            } else vehicleMarkerRef.current.setLngLat([gps.lng, gps.lat]);
          } catch (_) { if (!stopped) setTracking((value) => ({ ...value, status: 'offline', text: value.status === 'live' ? value.text : 'GPS location is unavailable.' })); }
          finally { inFlight = false; }
        };
        poll(); gpsTimerRef.current = setInterval(poll, 5000);
      } catch (gpsError) {
        if (stopped) return;
        const status = gpsError?.response?.status;
        setTracking({
          status: 'offline',
          text: status === 404
            ? 'No live vehicle location is available for this route.'
            : 'Vehicle location is currently unavailable.',
          updated: ''
        });
      }
    };
    startGps();
    return () => { stopped = true; if (gpsTimerRef.current) clearInterval(gpsTimerRef.current); gpsTimerRef.current = null; vehicleMarkerRef.current?.remove(); vehicleMarkerRef.current = null; stopMarkersRef.current.forEach((marker) => marker.remove()); stopMarkersRef.current = []; map.remove(); mapRef.current = null; };
  }, [selectedRoute, loadingStops, stops]);

  return (
    <section className="announcement-page">
      <header className="announcement-header">
        <div className="announcement-heading"><span className="announcement-heading-icon"><Megaphone size={24} /></span><div><span className="announcement-eyebrow">Audio management</span><h1>Route Announcement</h1><p>Manage stops and audio announcements by route.</p></div></div>
        <div className="announcement-header-stat"><Route size={18} /><span><strong>{routes.length}</strong> available routes</span></div>
      </header>
      {error && <div className="announcement-error" role="alert">{error}<button onClick={() => setError('')} aria-label="Dismiss"><X size={16} /></button></div>}

      {!selectedRoute ? <div className="announcement-panel">
        <div className="announcement-panel-head">
  <div>
    <h2>Available Routes</h2>
    <p>Select a route to view stops and generate audio.</p>
  </div>

  <div className="announcement-toolbar announcement-route-search">

    <label>
      <Search size={17} />

      <input
        type="search"
        value={routeQuery}
        onChange={(event) => {
          setRouteQuery(event.target.value);
          setRoutePage(1);
        }}
        placeholder="Search routes by name, ID or direction"
        aria-label="Search available routes"
      />

      {routeQuery && (
        <button
          onClick={() => {
            setRouteQuery('');
            setRoutePage(1);
          }}
          aria-label="Clear route search"
        >
          <X size={15} />
        </button>
      )}

    </label>

    {/* ADD ROUTE BUTTON */}

    <button
      className="ann-add-btn"
      onClick={openAddRouteDialog}
    >
      + Add Route
    </button>

  </div>
</div>
        {/* <div className="announcement-panel-head"><div><h2>Available Routes</h2><p>Select a route to view stops and generate audio.</p></div>
        <div className="announcement-toolbar announcement-route-search"><label><Search size={17} /><input type="search" value={routeQuery} onChange={(event) => { setRouteQuery(event.target.value); setRoutePage(1); }} placeholder="Search routes by name, ID or direction" aria-label="Search available routes" />{routeQuery && <button onClick={() => { setRouteQuery(''); setRoutePage(1); }} aria-label="Clear route search"><X size={15} /></button>}</label></div></div> */}
        {loading ? <SkeletonCards className="announcement-route-list" itemClassName="announcement-route" label="Loading routes..." /> : visibleRoutes.length === 0 ? <div className="announcement-state"><span className="announcement-empty-icon"><MapPinned size={30} /></span><h3>{routeQuery.trim() ? 'No matching routes' : 'No routes found'}</h3><p>{routeQuery.trim() ? 'Try a different search.' : 'Routes assigned to this dealer will appear here.'}</p></div> : <div className="announcement-route-list">{pageRoutes.map((route) => <article className="announcement-route" key={route.id} onClick={() => openRoute(route)}>
          <div className="announcement-route-main"><span className="announcement-avatar">{initials(route.route_name)}</span><div><h3>{route.route_name || `Route ${route.id}`}</h3><p>Route ID <strong>#{route.id}</strong>{route.direction ? ` · ${route.direction}` : ''}</p></div></div>
          <div className="announcement-route-actions"><button className="announcement-secondary" onClick={(event) => { event.stopPropagation(); downloadBin(route); }}><Download size={16} /> Download BIN</button><button className="announcement-primary" onClick={(event) => { event.stopPropagation(); openRoute(route); }}>View Stops <ChevronRight size={17} /></button></div>
        </article>)}</div>}
        {!loading && visibleRoutes.length > 0 && <div className="announcement-footer">
          {/* <span>Showing <strong>{(currentRoutePage - 1) * PAGE_SIZE + 1}?{Math.min(currentRoutePage * PAGE_SIZE, visibleRoutes.length)}</strong> of {visibleRoutes.length} routes</span> */}
          <div><button onClick={() => setRoutePage(currentRoutePage - 1)} disabled={currentRoutePage === 1}>Previous</button><span>Page {currentRoutePage} of {routePages}</span><button onClick={() => setRoutePage(currentRoutePage + 1)} disabled={currentRoutePage === routePages}>Next</button></div>
        </div>}
      </div> : <div className="announcement-panel">
        <div className="announcement-detail-head"><div><button className="announcement-back" onClick={() => { setSelectedRoute(null); setPlaying(null); audioRef.current?.pause(); }}><ArrowLeft size={17} /> All Routes</button><h2>{selectedRoute.route_name || `Route ${selectedRoute.id}`}</h2><p>{stops.length} stop{stops.length === 1 ? '' : 's'} · Route #{selectedRoute.id}</p></div><div className="announcement-detail-actions"><select value={language} onChange={(event) => setLanguage(event.target.value)} aria-label="Audio language"><option value="te">Telugu</option></select><button className="announcement-primary" onClick={generateRouteAudio} disabled={generating}><Sparkles size={16} /> {generating ? 'Generating...' : 'Generate Audio'}</button></div></div>
        <div className="announcement-route-detail"><div className="announcement-map-column"><div className="announcement-map" ref={mapContainerRef} /><div className={`announcement-gps-badge is-${tracking.status}`}><span className="announcement-live-dot" />{tracking.status === 'live' ? 'GPS Online · Live' : tracking.status === 'stale' ? 'Last known GPS position' : tracking.status === 'offline' ? 'GPS Offline' : 'Connecting to GPS'}</div><div className={`announcement-live-location is-${tracking.status}`}><span className="announcement-live-dot" /><div><small>Current vehicle position</small><strong>{tracking.text}</strong>{tracking.updated && <em>Updated {tracking.updated}</em>}</div></div></div><div className="announcement-stops-column">
        <div className="announcement-toolbar"><label><Search size={17} /><input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder="Search stops or landmarks" />{query && <button onClick={() => setQuery('')} aria-label="Clear search"><X size={15} /></button>}</label></div>
        {loadingStops ? <SkeletonTable headings={["#", "Stop name", "Audio"]} /> : visibleStops.length === 0 ? <div className="announcement-state"><span className="announcement-empty-icon"><MapPinned size={30} /></span><h3>{query ? 'No matching stops' : 'No stops found'}</h3><p>{query ? 'Try a different search.' : 'Stops added to this route will appear here.'}</p></div> : <div className="announcement-table-wrap"><table><thead><tr><th>#</th>
        <th>Stop name</th>
        <th>Audio</th></tr></thead><tbody>{pageStops.map((stop, index) => { const key = stop.id ?? stops.indexOf(stop); return <tr key={key}><td><span className="announcement-sequence">{String((page - 1) * PAGE_SIZE + index + 1).padStart(2, '0')}</span></td><td><strong>{stop.stop_name || 'Unnamed stop'}</strong></td><td><button className={`announcement-play ${playing === key ? 'is-playing' : ''}`} disabled={!audio[key]} onClick={() => toggleAudio(key)} aria-label={playing === key ? 'Pause audio' : 'Play audio'}>{playing === key ? <Pause size={15} /> : <Play size={15} />}</button></td></tr>; })}</tbody></table></div>}
        {visibleStops.length > 0 && <div className="announcement-footer"><span>Showing <strong>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, visibleStops.length)}</strong> of {visibleStops.length}</span><div><button onClick={() => setPage((value) => value - 1)} disabled={page === 1}>Previous</button><span>Page {page} of {pages}</span><button onClick={() => setPage((value) => value + 1)} disabled={page === pages}>Next</button></div></div>}
        </div></div>
      </div>}

      <div className="announcement-audio-card"><span className="announcement-audio-icon"><Volume2 size={23} /></span><div><h2>Generate Text Audio</h2><p>Create a standalone spoken announcement.</p></div><div className="announcement-audio-form"><input value={text} onChange={(event) => setText(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && generateTextAudio()} placeholder="Enter announcement text" /><select value={language} onChange={(event) => setLanguage(event.target.value)}><option value="te">Telugu</option></select><button onClick={generateTextAudio} disabled={!text.trim()}><Volume2 size={17} /> Generate</button></div></div>
    </section>
  );
};

export default Announcement;
